import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import Editor from '@diligentcorp/ckeditor5-assets';
import {
  undo,
  redo,
  bold,
  italic,
  underline,
  bulletedList,
  alignLeft,
  alignCenter,
  alignRight,
} from '@diligentcorp/atlas-theme-ckeditor';
import '@diligentcorp/atlas-theme-ckeditor/dist/index.css';
import { Spinner } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { TextField } from '@mui/material';
import {
  DESCRIPTION_MAX_LENGTH,
  getStrippedHtmlLength,
  getTrimmedLength,
  TITLE_MAX_LENGTH,
  validateDescription,
  validateTitle,
} from '../../helpers/validation';
import { FormCreationMode } from '../../types';
import {
  CardContentWrapper,
  CharacterCounter,
  CKEditorContainer,
  CollapseIcon,
  FieldLabelRow,
  FieldWrapper,
  FormField,
  FormLabel,
  LoadingStateContainer,
  MinimalCardContent,
  RequiredMark,
  SectionContainer,
  SectionHeader,
  SectionTitle,
  StyledErrorMessage,
  TitleDescriptionCard,
} from './FormTitleAndDescription.styles';

interface Props {
  mode: FormCreationMode;
  title: string;
  setTitle: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  titleTouched: boolean;
  setTitleTouched: (value: boolean) => void;
  descriptionTouched: boolean;
  setDescriptionTouched: (value: boolean) => void;
  readOnly?: boolean;
}

const CKEDITOR_CONFIG = {
  ...(Editor.defaultConfig as Record<string, unknown>),
  removePlugins: ['WordCount'],
  toolbar: {
    items: [
      'undo',
      'redo',
      '|',
      'heading',
      '|',
      'bold',
      'italic',
      'underline',
      '|',
      'bulletedList',
      '|',
      'alignment',
    ],
  },
  customIcons: {
    icons: {
      undo,
      redo,
      bold,
      italic,
      underline,
      bulletedList,
      alignLeft,
      alignCenter,
      alignRight,
    },
  },
  list: {
    properties: { styles: false },
  },
  wordCount: {
    displayWords: false,
    displayCharacters: false,
  },
};

export const FormTitleAndDescription = ({
  mode,
  title,
  setTitle,
  description,
  setDescription,
  titleTouched,
  setTitleTouched,
  descriptionTouched,
  setDescriptionTouched,
  readOnly = false,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleToggle = () => setIsExpanded(!isExpanded);

  const titleError = useMemo(() => {
    return titleTouched ? validateTitle(title) : null;
  }, [title, titleTouched]);

  const descriptionError = useMemo(() => {
    return descriptionTouched ? validateDescription(description) : null;
  }, [description, descriptionTouched]);

  useEffect(() => {
    if (titleError || descriptionError) {
      setIsExpanded(true);
    }
  }, [titleError, descriptionError]);

  const descriptionCharCount = useMemo(() => {
    return getStrippedHtmlLength(description);
  }, [description]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    if (!titleTouched) {
      setTitleTouched(true);
    }
  };

  const handleDescriptionChange = useCallback(
    (value: string) => {
      setDescription(value);
      if (!descriptionTouched) {
        setDescriptionTouched(true);
      }
    },
    [descriptionTouched, setDescription, setDescriptionTouched],
  );

  if (mode === 'initial') {
    return (
      <SectionContainer data-testid="form-title-description-section">
        <TitleDescriptionCard>
          <MinimalCardContent data-testid="form-title-description-initial-state">
            <SectionTitle data-testid="form-title-description-initial-title">
              Title and description
            </SectionTitle>
          </MinimalCardContent>
        </TitleDescriptionCard>
      </SectionContainer>
    );
  }

  if (mode === 'loading') {
    return (
      <SectionContainer data-testid="form-title-description-section">
        <TitleDescriptionCard>
          <LoadingStateContainer data-testid="form-title-description-loading-state">
            <Spinner variant="secondary" size="xlarge" />
          </LoadingStateContainer>
        </TitleDescriptionCard>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer data-testid="form-title-description-section">
      <TitleDescriptionCard $isCollapsed={!isExpanded}>
        <CardContentWrapper
          isCollapsed={!isExpanded}
          onClick={!isExpanded ? handleToggle : undefined}
        >
          <SectionHeader
            onClick={handleToggle}
            data-testid="form-title-description-collapse-toggle"
          >
            <CollapseIcon>
              <StyledIcon
                className={
                  isExpanded ? 'uil uil-angle-up' : 'uil uil-angle-down'
                }
                size="24px"
              />
            </CollapseIcon>
            <SectionTitle>Title and description</SectionTitle>
          </SectionHeader>
          {isExpanded && (
            <>
              <FormField>
                <FieldLabelRow>
                  <FormLabel data-testid="form-title-label">
                    Form title
                    <RequiredMark>*</RequiredMark>
                  </FormLabel>
                  <CharacterCounter
                    isError={getTrimmedLength(title) > TITLE_MAX_LENGTH}
                    data-testid="form-title-character-counter"
                    aria-live="polite"
                  >
                    {getTrimmedLength(title)}/{TITLE_MAX_LENGTH}
                  </CharacterCounter>
                </FieldLabelRow>
                <FieldWrapper data-testid="form-title-wrapper">
                  <TextField
                    fullWidth
                    value={title}
                    onChange={handleTitleChange}
                    placeholder="Enter form title"
                    disabled={readOnly}
                    error={!!titleError}
                    helperText={titleError || undefined}
                    slotProps={{
                      htmlInput: {
                        id: 'form-title-input',
                        'data-testid': 'form-title-input',
                        'aria-invalid': !!titleError,
                        'aria-describedby': titleError
                          ? 'form-title-error'
                          : undefined,
                      } as Record<string, unknown>,
                      formHelperText: {
                        id: 'form-title-error',
                        role: 'alert',
                        'data-testid': 'form-title-error-message',
                      } as Record<string, unknown>,
                    }}
                  />
                </FieldWrapper>
              </FormField>

              <FormField>
                <FieldLabelRow>
                  <FormLabel data-testid="form-description-label">
                    Form description
                  </FormLabel>
                  <CharacterCounter
                    isError={descriptionCharCount > DESCRIPTION_MAX_LENGTH}
                    data-testid="form-description-character-counter"
                    aria-live="polite"
                  >
                    {descriptionCharCount}/{DESCRIPTION_MAX_LENGTH}
                  </CharacterCounter>
                </FieldLabelRow>
                <FieldWrapper data-testid="form-description-wrapper">
                  <CKEditorContainer
                    className={descriptionError ? 'ck-editor-error' : undefined}
                    data-testid="form-description-editor"
                  >
                    <CKEditor
                      config={CKEDITOR_CONFIG}
                      editor={Editor}
                      data={description}
                      disabled={readOnly}
                      onReady={(editor) => {
                        if (readOnly) {
                          editor.enableReadOnlyMode('readonly-mode');
                        }
                      }}
                      onChange={(_event, editor) => {
                        handleDescriptionChange(editor.getData());
                      }}
                    />
                  </CKEditorContainer>
                  {descriptionError && (
                    <StyledErrorMessage
                      id="form-description-error"
                      role="alert"
                      data-testid="form-description-error-message"
                    >
                      {descriptionError}
                    </StyledErrorMessage>
                  )}
                </FieldWrapper>
              </FormField>
            </>
          )}
        </CardContentWrapper>
      </TitleDescriptionCard>
    </SectionContainer>
  );
};
